<template>
  <Card>
    <template #title>
      <div class="text-2xl">Step 3: Summary & Export</div>
      <div class="text-sm text-500 mt-2">
        Group: <strong>{{ group?.name || "N/A" }}</strong> (ID: {{ groupId }})
      </div>
    </template>
    <template #content>
      <div v-if="creating" class="text-center p-5">
        <ProgressSpinner />
        <p class="mt-3">Creating sellers...</p>
      </div>

      <div v-else-if="createdSellers.length > 0">
        <!-- Sellers Table -->
        <DataTable :value="createdSellers" responsiveLayout="scroll">
          <Column field="id" header="Seller ID" :sortable="true">
            <template #body="slotProps">
              <Tag :value="slotProps.data.id" severity="info" />
            </template>
          </Column>
          <Column field="name" header="Name" />
          <Column field="domain" header="Domain" />
          <Column field="seller_type" header="Type">
            <template #body="slotProps">
              {{
                slotProps.data.seller_type === "audio"
                  ? "Audio Only"
                  : "Audio + Video"
              }}
            </template>
          </Column>
          <Column field="content_media_type" header="Media Type">
            <template #body="slotProps">
              <Tag
                :icon="
                  slotProps.data.content_media_type === 'snippet'
                    ? 'pi pi-bolt'
                    : 'pi pi-microphone'
                "
                :severity="
                  slotProps.data.content_media_type === 'snippet'
                    ? 'warning'
                    : 'info'
                "
                :value="
                  slotProps.data.content_media_type === 'snippet'
                    ? 'Snippet'
                    : 'Podcast'
                "
              />
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button
                :label="
                  slotProps.data.content_media_type === 'snippet'
                    ? 'Generate Player'
                    : 'Generate Player & Ads.txt'
                "
                icon="pi pi-download"
                size="small"
                @click="openExportDialog(slotProps.data)"
              />
            </template>
          </Column>
        </DataTable>

        <!-- Success Message -->
        <Message severity="success" class="mt-4">
          <template #default>
            Successfully created {{ createdSellers.length }} seller(s). You can
            now generate player code
          </template>
        </Message>
      </div>

      <div v-else class="text-center p-5 text-500">
        <i class="pi pi-info-circle" style="font-size: 2rem"></i>
        <p class="mt-3">No sellers created yet</p>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-content-between gap-2">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          outlined
          @click="$emit('back')"
          :disabled="creating"
        />
        <Button
          label="Finish"
          icon="pi pi-check"
          iconPos="right"
          @click="$emit('finish')"
          :disabled="creating"
        />
      </div>
    </template>
  </Card>

  <!-- Player Export Dialog -->
  <PlayerExportDialog
    v-model:visible="showExportDialog"
    v-if="selectedSeller?.content_media_type !== 'snippet'"
    :seller="selectedSeller"
  />

  <GenerateSnippetTagDialog
    v-model:visible="showExportDialog"
    v-if="selectedSeller?.content_media_type === 'snippet'"
    :selectedSeller="selectedSeller"
    :partner="{ id: selectedSeller?.partner_id }"
    @close-dialog="showExportDialog = false"
    @hide="showExportDialog = false"
  />
</template>

<script setup>
import { ref, onMounted, defineProps } from "vue";
import { useToast } from "primevue/usetoast";
import axios from "axios";
import { API_BASE_URL } from "@/consts";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Message from "primevue/message";
import ProgressSpinner from "primevue/progressspinner";
import Tag from "primevue/tag";
import PlayerExportDialog from "./PlayerExportDialog.vue";
import GenerateSnippetTagDialog from "../GenerateSnippetTagDialog.vue";

const props = defineProps({
  group: {
    type: Object,
    default: null,
  },
  groupId: {
    type: Number,
    default: null,
  },
  groupMode: {
    type: String,
    default: "existing",
  },
  sellers: {
    type: Array,
    required: true,
  },
});

const toast = useToast();

const creating = ref(false);
const createdSellers = ref([]);
const showExportDialog = ref(false);
const selectedSeller = ref(null);

const createSellers = async () => {
  try {
    creating.value = true;
    const token = localStorage.getItem("adminToken");
    let createdGroupId = props.groupId;

    // Get partnerId from group (needed for seller creation)
    let partnerId = null;

    // Step 1: Create group if mode === "new"
    if (props.groupMode === "new") {
      // ✅ Get partner_id BEFORE creating group (it's already in props.group)
      partnerId = props.group?.partner_id;

      if (!partnerId) {
        throw new Error("Partner ID is missing for new group");
      }

      const groupPayload = {
        ...props.group,
        partner_id: partnerId,
        status: "active", // Auto-approve
      };

      const groupResponse = await axios.post(
        `${API_BASE_URL}/seller-group`,
        groupPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            partnerid: partnerId, // Use partner_id from props
          },
        }
      );

      if (!groupResponse.data?.success) {
        const errorMessage =
          groupResponse.data?.message || "Failed to create group";
        throw new Error(errorMessage);
      }

      createdGroupId = groupResponse.data.groupId;

      if (!createdGroupId) {
        throw new Error("Failed to get group ID after creation");
      }
    } else {
      // For existing group, fetch group data to get partner_id
      if (!createdGroupId) {
        throw new Error("Group ID is missing for existing group");
      }

      const existingGroupResponse = await axios.get(
        `${API_BASE_URL}/seller-group/${createdGroupId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      partnerId = existingGroupResponse.data.sellerGroup?.partner_id;

      if (!partnerId) {
        throw new Error("Failed to get partner_id from existing group");
      }
    }

    // Step 2: Create sellers one by one
    const successfulSellers = [];
    const failedSellers = [];

    for (const seller of props.sellers) {
      try {
        const sellerPayload = {
          name: seller.name,
          email: props.group?.email || "", // Use group's email
          domain: seller.domain,
          contentType: seller.content_type || "shows",
          contentMediaType: seller.content_media_type || "podcast",
          restrictedValidation: !!seller.restricted_validation,
          contentAllocationConfig: seller.content_allocation_config || null,
          contractType: seller.contract_type,
          contractValue: seller.contract_value,
          sellerGroup: createdGroupId,
          categories: seller.categories || [],
          show_ids_content: seller.show_ids_content?.length
            ? JSON.stringify(seller.show_ids_content)
            : null,
          password: seller.password,
          status: "approved",
          skip_connatix_check: true, // ← Bypass Connatix validation
        };

        // Only add connatix_player_id if it has value
        if (seller.connatix_player_id && seller.connatix_player_id.trim()) {
          sellerPayload.connatix_player_id = seller.connatix_player_id;
        }

        // Add connatix_player_share if seller type is audio_video
        if (
          seller.seller_type === "audio_video" &&
          seller.connatix_player_share
        ) {
          sellerPayload.connatix_player_share = seller.connatix_player_share;
        }

        const sellerResponse = await axios.post(
          `${API_BASE_URL}/add-seller`,
          sellerPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              partnerid: partnerId, // Required for admin role
            },
          }
        );

        if (sellerResponse.data?.success) {
          const sellerId =
            sellerResponse.data.seller?.id ||
            sellerResponse.data.sellerId ||
            sellerResponse.data.seller ||
            sellerResponse.data.id;

          successfulSellers.push({
            ...seller,
            id: sellerId,
            partner_id: partnerId, // Add partner_id for player generation
            seller_group_id: createdGroupId, // Add seller_group_id for ads.txt fetch
          });
        } else {
          failedSellers.push(seller.name);
        }
      } catch (sellerError) {
        console.error(`Failed to create seller ${seller.name}:`, sellerError);
        failedSellers.push(seller.name);
      }
    }

    // Rollback: If all sellers failed and we created a new group, delete it
    if (
      failedSellers.length === props.sellers.length &&
      props.groupMode === "new"
    ) {
      try {
        await axios.delete(`${API_BASE_URL}/seller-group/${createdGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.add({
          severity: "error",
          summary: "Creation Failed",
          detail: "All sellers failed. Group has been rolled back.",
          life: 5000,
        });
      } catch (rollbackError) {
        console.error("Rollback failed:", rollbackError);
      }
      return;
    }

    // Show results
    createdSellers.value = successfulSellers;

    if (successfulSellers.length > 0) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: `Successfully created ${successfulSellers.length} seller(s)`,
        life: 4000,
      });
    }

    if (failedSellers.length > 0) {
      toast.add({
        severity: "warn",
        summary: "Partial Success",
        detail: `${failedSellers.length} seller(s) failed: ${failedSellers.join(
          ", "
        )}`,
        life: 6000,
      });
    }
  } catch (error) {
    console.error("Error in onboarding:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error.message || "Failed to complete onboarding",
      life: 4000,
    });
  } finally {
    creating.value = false;
  }
};

const openExportDialog = (seller) => {
  selectedSeller.value = seller;
  showExportDialog.value = true;
};

onMounted(() => {
  createSellers();
});
</script>

<style scoped>
:deep(.p-datatable) {
  font-size: 0.9rem;
}
</style>
